import { FEATURE_FLAGS } from '@brandschutz/shared';
import { Icon } from '@/lib/icons';
import { cn } from '@/lib/cn';
import { useIsFeatureEnabled } from '@/lib/feature-flags';
import { useToast } from '@/components/ui/Toast';
import { useSprachsteuerung } from './context';
import { FEHLER_TEXTE, spracherkennungVerfuegbar } from './web-speech';

/**
 * Knopf in der TopBar (Slot `global.topbar.actions`).
 *
 * Kann der Browser keine Spracherkennung, bleibt der Knopf sichtbar, aber
 * abgeblendet und erklärt beim Auswählen, warum. Ein einfach fehlender Knopf
 * ließe den Nutzer im Unklaren, ein `disabled`-Knopf wäre per Tastatur nicht
 * erreichbar — deshalb `aria-disabled` statt `disabled`.
 */
export function SprachsteuerungTrigger() {
  const aktiviert = useIsFeatureEnabled(FEATURE_FLAGS.VOICE_CONTROL);
  const { offen, umschalten } = useSprachsteuerung();
  const toast = useToast();

  if (!aktiviert) return null;

  const verfuegbar = spracherkennungVerfuegbar();
  const titel = verfuegbar
    ? offen
      ? 'Sprachsteuerung schließen (Alt+S)'
      : 'Sprachsteuerung öffnen (Alt+S)'
    : 'Sprachsteuerung nicht verfügbar';

  return (
    <button
      type="button"
      onClick={() => {
        if (!verfuegbar) {
          toast.info('Sprachsteuerung', FEHLER_TEXTE['nicht-unterstuetzt']);
          return;
        }
        umschalten();
      }}
      aria-pressed={verfuegbar ? offen : undefined}
      aria-disabled={!verfuegbar}
      aria-label={titel}
      title={titel}
      className={cn(
        'inline-flex h-11 w-11 items-center justify-center rounded transition-colors md:h-9 md:w-9',
        !verfuegbar && 'text-ink-400',
        verfuegbar && offen
          ? 'text-accent-700 hover:bg-accent-100 dark:text-accent-400'
          : 'text-ink-500 hover:bg-ink-100',
      )}
    >
      <Icon
        name={verfuegbar ? 'Mic' : 'MicOff'}
        size={18}
        className={cn(offen && 'animate-pulse')}
      />
    </button>
  );
}
